import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CLOUDFLARE_SOFTBLOCK } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { useAppDispatch } from 'App/store';
import {
  getCloudflareBlocked,
  getCloudflareHardBlocked,
} from 'domains/Auth/authSlice/authSelectors';
import { useRefreshInfoToSync } from 'domains/Auth/hooks';
import { CLOUDFLARE_BLOCK_DISMISS_EXPIRATION_TTL_IN_SECONDS } from 'domains/Dashboard/slices/constants';
import dashboardService from '../../services';
import {
  checkCloudflareDismissAction,
  getCloudflareDismissCheckData,
  getCloudflareDismissCheckStatus,
} from '../../slices/usersSlice';

function useCloudflareBlocked() {
  const dispatch = useAppDispatch();
  const isCloudflareBlocked = useSelector(getCloudflareBlocked);
  const [cloudflareBlockedFeatureEnabled] = useHasTags(CLOUDFLARE_SOFTBLOCK);
  const dismissCheckData = useSelector(getCloudflareDismissCheckData);
  const dismissCheckStatus = useSelector(getCloudflareDismissCheckStatus);
  const ttl = CLOUDFLARE_BLOCK_DISMISS_EXPIRATION_TTL_IN_SECONDS;

  const baseCloudflareBlocked =
    isCloudflareBlocked && cloudflareBlockedFeatureEnabled;

  useEffect(() => {
    if (baseCloudflareBlocked && dismissCheckStatus === 'idle') {
      dispatch(checkCloudflareDismissAction());
    }
  }, [baseCloudflareBlocked, dismissCheckStatus, dispatch]);

  const cloudflareBlocked = useMemo(() => {
    if (!baseCloudflareBlocked) {
      return false;
    }

    if (
      ['loading', 'idle', 'error'].includes(dismissCheckStatus) ||
      !dismissCheckData
    ) {
      return true;
    }

    const { dismissExpiresAt } = dismissCheckData;
    const now = Math.floor(Date.now() / 1000);

    return dismissExpiresAt === null || now >= dismissExpiresAt;
  }, [baseCloudflareBlocked, dismissCheckStatus, dismissCheckData]);

  const hardBlock = useSelector(getCloudflareHardBlocked);
  const { refreshInfo } = useRefreshInfoToSync();

  const dismissCloudflareBlock = async () => {
    const timestampNow = Math.floor(Date.now() / 1000);
    await dashboardService.dismissCloudflareBlock(timestampNow + ttl);

    refreshInfo(true);
    dispatch(checkCloudflareDismissAction());
  };

  const validateIPs = async () => {
    const { migrationSuccess } =
      await dashboardService.cloudflareIPsMigrationCheck();

    return migrationSuccess;
  };

  return {
    cloudflareBlocked,
    hardBlock,
    dismissCloudflareBlock,
    validateIPs,
  };
}

export default useCloudflareBlocked;
